import { Injectable, OnDestroy } from "@angular/core";
import { PriceyardService } from "../modules/priceyard/services/priceyard.service";
import { GridActionsService } from "./grid-actions.service";
import { SkuGridBaseConfigService } from "./sku-grid-base-config.service";
import { ShipyardService } from "../modules/shipyard/shipyard.service";
import { GridCheckboxHeaderRendererComponent } from "../ag-grid-components/grid-checkbox-header-renderer/grid-checkbox-header-renderer.component";
import { DatePipe } from "@angular/common";
import { ICellRendererParams } from "@ag-grid-community/core";

import { Subject, takeUntil } from "rxjs";

@Injectable({
  providedIn: "root",
})
export class PriceyardSkuGridConfigService implements OnDestroy {
  columnDefs;
  gridOptions;
  repriceModels$ = this.priceyardService.GetPriceModel();
  options: [];
  priceCalColDef;
  private destroy$ = new Subject<void>();

  filterParams = {
    defaultOption: "inRange",
    maxNumConditions : 1,
    filterOptions: [
      "equals",
      "notEqual",
      "lessThan",
      "lessThanOrEqual",
      "greaterThan",
      "greaterThanOrEqual",
      {
        displayKey: "inRange",
        displayName: "Between",
        test: {},
      },
    ],
  };
  
  feelMonitorColDef: any = [
    {
      field: "",
      backendFields: ["AccountSkuId"],
      checkboxSelection: true,
      minWidth: 50,
      resizable: false,
      pinned: "left",
      lockPosition: true,
      suppressColumnsToolPanel: true,
      headerComponent: GridCheckboxHeaderRendererComponent,
    },
    {
      headerName: "Date",
      field: "Date",
      headerTooltip: "Date",
      backendFields: ["Date"],
      valueFormatter: (params) => {
        return params.value
          ? new DatePipe("en-US").transform(params.value, "MM/dd/YYYY")
          : "";
      },
      minWidth: 130,
    },
    {
      field: "Sku",
      backendFields: ["image", "ProductName", "Asin", "Sku"],
      headerName: "Product",
      headerTooltip: 'Product',
      minWidth: 300,
      cellRenderer: "skuCellRendererComponent",
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
            display: "accordion",
            title: "SKU",
            filterParams: {
              defaultOption: "contains",
              maxNumConditions : 1,
            },
          },
          {
            filter: "agTextColumnFilter",
            display: "accordion",
            title: "ASIN",
            filterParams: {
              defaultOption: "contains",
              maxNumConditions : 1,
            },
          },
          {
            display: "accordion",
            title: "Description",
            filter: "agTextColumnFilter",
            filterParams: {
              defaultOption: "contains",
              maxNumConditions : 1,
            },
          },
        ],
      },
    },
    {
      field: "PreviousEstimatedFeeTotal",
      headerName: "Previous Fee",
      backendFields: ["PreviousEstimatedFeeTotal"],
      headerTooltip: "Previous Fee",
      minWidth: 130,
      filter: "agNumberColumnFilter",
      cellRenderer: "numberAndTrendCellRendererComponent",
      cellRendererParams: {
        props: {
          numberProp: "PreviousEstimatedFeeTotal",
          dollarProp: true,
        },
      },
      valueFormatter: (params) => Number(params.data.PreviousEstimatedFeeTotal),
    },
    {
      field: "EstimatedFeeTotal",
      headerName: "New Fee",
      backendFields: ["EstimatedFeeTotal"],
      headerTooltip: "New Fee",
      minWidth: 130,
      filter: "agNumberColumnFilter",
      cellRenderer: "numberAndTrendCellRendererComponent",
      cellRendererParams: {
        props: {
          numberProp: "EstimatedFeeTotal",
          dollarProp: true,
        },
      },
      valueFormatter: (params) => Number(params.data.EstimatedFeeTotal),
    },
    {
      field: "fee30d",
      backendFields: ["fee30d", "fee60d"],
      headerName: "Fee Changes",
      headerTooltip: "Fee Changes",
      minWidth: 130,
      cellRenderer: "simpleListCellRendererComponent",
      cellRendererParams: {
        isFeeMonitor: true,
        props: [
          { label: "30d", priceProp: "fee30d" },
          { label: "60d", priceProp: "fee60d" },
        ],
        displayArrow: true,
      },
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agNumberColumnFilter",
            display: "accordion",
            title: "30d",
            filterParams: this.filterParams,
          },
          {
            filter: "agNumberColumnFilter",
            display: "accordion",
            title: "60d",
            filterParams: this.filterParams,
          },
        ],
      },
    },
    {
      field: "PreviousItemPackageWeight",
      headerName: "Weight",
      backendFields: ["PreviousItemPackageWeight", "ItemPackageWeight"],
      headerTooltip: "Weight",
      minWidth: 130,
      cellRenderer: "dimentionsCellRendererComponent",
      cellRendererParams: {
        props: {
          previousValue: "PreviousItemPackageWeight",
          afterValue: "ItemPackageWeight",
          unit: "kg",
        },
      }, 
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agNumberColumnFilter",
            display: "accordion",
            title: "Previous Weight",
            filterParams: this.filterParams,
          },
          {
            filter: "agNumberColumnFilter",
            display: "accordion",
            title: "After Weight",
            filterParams: this.filterParams,
          },
        ],
      },     
    },
    {
      field: "PreviousMedianSide",
      headerName: "Height",
      backendFields: ["PreviousMedianSide", "MedianSide"],
      headerTooltip: "Height",
      minWidth: 130,
      cellRenderer: "dimentionsCellRendererComponent",
      cellRendererParams: {
        props: {
          previousValue: "PreviousMedianSide",
          afterValue: "MedianSide",
          unit: "cm",
        },
      },
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agNumberColumnFilter",
            display: "accordion",
            title: "Previous Height",
            filterParams: this.filterParams,
          },
          {
            filter: "agNumberColumnFilter",
            display: "accordion",
            title: "After Height",
            filterParams: this.filterParams,
          },
        ],
      },    
    },
    {
      field: "PreviousLongestSide",
      headerName: "Length",
      backendFields: ["PreviousLongestSide", "LongestSide"],
      headerTooltip: "Length",
      minWidth: 130,
      cellRenderer: "dimentionsCellRendererComponent",
      cellRendererParams: {
        props: {
          previousValue: "PreviousLongestSide",
          afterValue: "LongestSide",
          unit: "cm",
        },
      },
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agNumberColumnFilter",
            display: "accordion",
            title: "Previous Length",
            filterParams: this.filterParams,
          },
          {
            filter: "agNumberColumnFilter",
            display: "accordion",
            title: "After Length",
            filterParams: this.filterParams,
          },
        ],
      }, 
    },
    {
      field: "PreviousShortestSide",
      headerName: "Width",
      backendFields: ["PreviousShortestSide", "ShortestSide"],
      headerTooltip: "Width",
      minWidth: 130,
      cellRenderer: "dimentionsCellRendererComponent",
      cellRendererParams: {
        props: {
          previousValue: "PreviousShortestSide",
          afterValue: "ShortestSide",
          unit: "cm",
        },
      },
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agNumberColumnFilter",
            display: "accordion",
            title: "Previous Width",
            filterParams: this.filterParams,
          },
          {
            filter: "agNumberColumnFilter",
            display: "accordion",
            title: "After Width",
            filterParams: this.filterParams,
          },
        ],
      }, 
    },
  ];

  constructor(
    private skuGridBaseConfigService: SkuGridBaseConfigService,
    private gridActionsService: GridActionsService,
    private priceyardService: PriceyardService,
    private shipyardService: ShipyardService
  ) {
    this.gridOptions = this.skuGridBaseConfigService.gridOptions;   
    this.columnDefs = [...this.skuGridBaseConfigService.columnDefs];

    this.columnDefs.splice(
      7,
      0,
      
      {
        field: "TotalFbastock",
        headerName: "Total Stock",
        headerTooltip: 'Total Stock',
        width: 130,
        minWidth: 50,
        backendFields: ["FbaStock"],
        sortable: true,
        filter: "agNumberColumnFilter",
        valueGetter: (params) => {
          return (Number(params.data.FbaStock) || 0).toLocaleString('en');
        },
        cellClass: params => {
          return !params.value ? 'gray' : null;
        },
      },
      {
        field: "OverStock",
        headerName: "OverStock Level",
        headerTooltip: 'OverStock Level',
        width: 130,
        minWidth: 50,
        backendFields: ["OverStock"],
        sortable: true,
        filter: "agNumberColumnFilter",
        valueGetter: (params) => {
          return (Number(params.data.OverStock) || 0).toLocaleString('en');
        },
        cellClass: params => {
          return !params.value ? 'gray' : null;
        },
      },
      {
        field: "Rank",
        headerName:"Category/Rank",
        headerTooltip:"Category/Rank",
        backendFields: ["Rank", "Category"],
        hide: true,
        sortable: true,
        cellRenderer: "rankCellRendererComponent",
        filter: "agMultiColumnFilter",
        filterParams: {
          filters: [
            {
              filter: "agSetColumnFilter",
              display: "accordion",
              title: "Category",
              filterParams: {
                values: (params) => this.gridActionsService.categoryList$.pipe(takeUntil(this.destroy$)).subscribe((e: any) => params.success(e)),
              },
            },
            {
              filter: "agNumberColumnFilter",
              display: "accordion",
              title: "Rank",
              filterParams: this.filterParams,
            },
          ],
        },
      },
      {
        field: "FbaType",
        headerName: "FBA Type",
        headerTooltip:"FBA Type",
        backendFields: ["FbaType", "FbaTypeId"],
        filter: "agSetColumnFilter",
        required:true,
        hide: true,
        filterParams: {
          values: params => params.success(this.shipyardService.getAllShipmentTypeNames()),
        },
      },
      {
        field: "FulfillmentType",
        backendFields: ["FulfillmentType"],
        headerName: "Fulfillment",
        headerTooltip:"Fulfillment",
        width: 100,
        sortable: true,
        hide: true,
      },
      {
        field: "OffersFba",
        headerName: "Offers",
        headerTooltip:"Offers",        
        backendFields: ["OffersFba", "OffersFbm"],
        width: 100,
        cellRenderer: "offersCellRendererComponent",
        filter: "agMultiColumnFilter",
        filterParams: {
          filters: [
            {
              filter: "agNumberColumnFilter",
              display: "accordion",
              title: "Fba",
              filterParams: this.filterParams,
            },
            {
              filter: "agNumberColumnFilter",
              display: "accordion",
              title: "Fbm",
              filterParams: this.filterParams,
            },
          ],
        },
      },
      {
        headerName: "Price Range",
        headerTooltip:"Price Range",
        backendFields: [
          "MinPrice",
          "MaxPrice",
          "Minpr",
          "MinprIsManual",
          "Maxpr",
          "MaxprIsManual",
          "DefaultPrice",
          "BreakEvenPrice"
        ],
        field: "PriceRange",
        cellRenderer: "multiPriceCellRendererComponent",
        sortable:false,
        cellRendererParams: {
          props: [
            { label: "Min", priceProp: "MinPrice", autoProp: "MinprIsManual" },
            { label: "Max", priceProp: "MaxPrice", autoProp: "MaxprIsManual" },
            { label: "Default", priceProp: "DefaultPrice" },
            { label: "Breakeven", priceProp: "BreakEvenPrice", autoProp: "MaxprIsManual" }
          ],
        },
        filter: "agMultiColumnFilter",
        filterParams: {
          filters: [
            {
              filter: "agNumberColumnFilter",
              display: "accordion",
              title: "Min Price",
              filterParams: this.filterParams,
            },
            {
              filter: "agNumberColumnFilter",
              display: "accordion",
              title: "Max Price",
              filterParams: this.filterParams,
            },
            {
              filter: "agNumberColumnFilter",
              display: "accordion",
              title: "Default",
              filterParams: this.filterParams,
            },
          ],
        },
      },
      {
        headerName: "Cost",
        headerTooltip:"Cost",
        backendFields: ["Cost", "PrepCost", "PrepCostIsAuto"],
        hide: true,
        field: "Cost",
        cellRenderer: "multiPriceCellRendererComponent",
        cellRendererParams: {
          props: [
            { label: "Cost", priceProp: "Cost" },
            {
              label: "Prep Cost",
              priceProp: "PrepCost",
              autoProp: "PrepCostIsAuto",
            },
          ],
        },
        filter: "agMultiColumnFilter",
        filterParams: {
          filters: [
            {
              filter: "agNumberColumnFilter",
              display: "accordion",
              title: "Cost",
              filterParams: this.filterParams,
            },
            {
              filter: "agNumberColumnFilter",
              display: "accordion",
              title: "PrepCost",
              filterParams: this.filterParams,
            },
          ],
        },
      },
      {
        headerName: "Markup",
        headerTooltip:"Markup",
        backendFields: [
          "Markup",
          "MarkupIsAuto",
          "MinMarkup",
          "MinMarkupIsAuto",
          "MarkupType"
        ],
        hide: true,
        field: "Markup",
        cellRenderer: "multiPriceCellRendererComponent",
        cellRendererParams: {
          props: [
            { label: "Markup", priceProp: "Markup", autoProp: "MarkupIsAuto", type: "MarkupType" },
            {
              label: "Min Markup",
              priceProp: "MinMarkup",
              autoProp: "MinMarkupIsAuto",
            },
          ],
        },
        filter: "agMultiColumnFilter",
        filterParams: {
          filters: [
            {
              filter: "agNumberColumnFilter",
              display: "accordion",
              title: "Markup",
              filterParams: this.filterParams,
            },
            {
              filter: "agNumberColumnFilter",
              display: "accordion",
              title: "MinMarkup",
              filterParams: this.filterParams,
            },
          ],
        },
      },
      {
        headerName: "MAP",
        headerTooltip:"MAP",
        backendFields: ["MapPrice", "MapIsActive"],
        hide: true,
        field: "Map",
        cellRenderer: "singlePriceCellRendererComponent",
        cellRendererParams: {
          props: "MapPrice",
        },
        filter: "agMultiColumnFilter",
        filterParams: {
          filters: [
            {
              filter: "agNumberColumnFilter",
              display: "accordion",
              title: "Price",
              filterParams: this.filterParams,
            },
            {
              filter: "booleanFilterComponent",
              display: "accordion",
              title: "MapIsActive",
            },
          ],
        },
      },
      {
        field: "Prmodel",
        headerTooltip:"Repricing Model",
        backendFields: [
          "Prmodel",
          "PriceModelName",
          "Prcfreeze",
          "IsRepricing",
          "PricingOn",
        ],

        headerName: "Repricing Model",
        cellRenderer: "repricingModelCellRendererComponent",
        cellRendererParams: [this.repriceModels$],
        cellEditor: "repricingModelCellEditorComponent",
        cellEditorParams: [this.repriceModels$],
        editable: true,
        sortable: true,
        width: 220,
        minWidth: 175,
        onCellValueChanged: (event) =>
          this.gridActionsService.updateSkuPricingModel(event),
        filter: "agMultiColumnFilter",
        filterParams: {
          filters: [
            {
              filter: "agSetColumnFilter",
              display: "accordion",
              title: "Repricing Model",
              filterParams: {
                values: (params) => {
                  this.repriceModels$.pipe(takeUntil(this.destroy$)).subscribe((e: any) => {
                    params.success([...e.map((e) => e.name), "Unassigned"]);
                  });
                },
                comparator: (a, b) => {
                  if (a === "Unassigned") {
                    return -1;
                  }
                },
              },
            },
            {
              filter: "booleanFilterComponent",
              display: "accordion",
              title: "Is Repricing",
            },
          ],
        },
      },
      {
        field: "Repriced",
        headerTooltip:"Repriced",
        backendFields: ["Repriced"],
        headerName: "Last Repriced",
        width: 140,
        sortable: true,
        hide: true,
        cellRenderer: "dateCellRendererComponent",
        filter: "agDateColumnFilter",
      },
      {
        field: "Bbupdated",
        headerTooltip:"Bbupdated",
        backendFields: ["Bbupdated"],
        headerName: "Bbupdated",
        width: 140,
        sortable: true,
        hide: true,
        cellRenderer: "dateCellRendererComponent",
        filter: "agDateColumnFilter",
      },
      {
        field: "PricingOn",
        headerTooltip:"PricingOn",
        backendFields: ["PricingOn"],
        headerName: "PricingOn",
        width: 140,
        sortable: true,
        hide: true,
      }
    );

    
  }

  priceCalculateColDef(){
    this.priceCalColDef = [
      {
        headerName: "Imported List",
        headerClass: "import-list separate-border justify-content-center",
        children: [
          {
            field: "Asin",
            backendFields: [
              "Image",
              "Sku",
              "Asin",
              "Title",
              "AccountType",
              "FulfillmentType",
            ],
            headerName: "ASIN",
            headerTooltip: "ASIN",
            filter: "agTextColumnFilter",
            cellRendererSelector: (params) => {
              const validSku = {
                component: "skuCellRendererComponent",
              };
              return !params.data.isError ? validSku : null;
            },
            cellRenderer: function (params: ICellRendererParams) {
              if (params.data.isError){
                return `<div class="calculate-validation">The Asin are <span>` + params.data.asin + `</span> invalid.</div>`
              }
            },
            minWidth: 250,
            headerClass: "import-list",
            valueFormatter: (params) => {
              params.data["Asin"] = params.data.asin;
              params.data["Title"] = params.data.title;
              params.data["Image"] = params.data.image;
            },
            cellClass: (params) => {
              return params.data.isError
                ? "border-right-cell justify-content-center"
                : null;
            },
            colSpan: (params) => {
              return params.data.isError ? 5 : 0;
            },
          },
          {
            headerName: "Item Cost",
            field: "itemCost",
            headerTooltip: "ItemCost",
            headerClass: "import-list",
            filter: "agNumberColumnFilter",
            valueFormatter: (params) => {
              return  '$' + params.value;
            }
          },
          {
            headerName: "Amount",
            field: "profitAmount",
            colId: "profitAmount",
            headerClass: "import-list",
            filter: "agNumberColumnFilter",
            valueFormatter: (params) => {
              return  '$' + params.value;
            }
          },
          {
            headerName: "Margin",
            field: "profitMargin",
            colId: "profitMargin",
            headerClass: "import-list",
            filter: "agNumberColumnFilter",
            valueFormatter: (params) => {
              return params.value + '%';
            }
          },
          {
            headerName: "Markup",
            field: "profitMarkup",
            colId: "profitMarkup",
            headerClass: "import-list separate-border",
            filter: "agNumberColumnFilter",
            cellClass: (params) => {
              return !params.data.isError ? "border-right-cell" : null;
            },
            valueFormatter: (params) => {
              return params.value + '%';
            }
          },
        ],
      },
      {
        headerName: "FBA Fees",
        headerClass: "fba-fees separate-border justify-content-center",
        children: [
          {
            field: "fbaFullfillmentFees",
            backendFields: ["fbaFullfillmentFees"],
            headerName: "fulfilment fee",
            headerTooltip: "fulfilment fee",
            headerClass: "fba-fees",
            filter: "agNumberColumnFilter",
            cellRenderer: function (params: ICellRendererParams) {
              if (params.value !== undefined && !params.data.isError) {
                return '$' + params.value;
              } else if(params.data.isError){
                return `<div class="calculate-validation">The Asin are <span>` + params.data.asin + `</span> invalid.</div>`
              } else {
                return getImage();
              }
            },
            cellClass: (params) => {
              return params.data.isError
                ? "border-right-cell justify-content-center"
                : null;
            },
            colSpan: (params) => {
              return params.data.isError ? 5 : 0;
            },
          },
          {
            headerName: "Shipment fee",
            field: "fbaPromotionFees",
            filter: "agNumberColumnFilter",
            headerClass: "fba-fees",
            cellRenderer: function (params: ICellRendererParams) {
              if (params.value !== undefined) {
                return '$' + params.value;
              } else {
                return getImage();
              }
            },
          },
          {
            headerName: "Total fee",
            field: "fbaTotalFees",
            filter: "agNumberColumnFilter",
            headerClass: "fba-fees",
            cellRenderer: function (params: ICellRendererParams) {
              if (params.value !== undefined) {
                return '$' + params.value;
              } else {
                return getImage();
              }
            },
          },
          {
            headerName: "Break-even price",
            field: "breakEvenPrice.breakEvenPriceForFba",
            filter: "agNumberColumnFilter",
            headerClass: "fba-fees",
            cellRenderer: function (params: ICellRendererParams) {
              if (params.value !== undefined) {
                return '$' + params.value;
              } else {
                return getImage();
              }
            },
          },
          {
            headerName: "Min Price",
            field: "minPrice.minPriceForFba",
            filter: "agNumberColumnFilter",
            headerClass: "fba-fees separate-border",
            cellRenderer: function (params: ICellRendererParams) {
              if (params.value !== undefined) {
                return '$' + params.value;
              } else {
                return getImage();
              }
            },
            cellClass: (params) => {
              return !params.data.isError ? "border-right-cell" : null;
            },
          },
        ],
      },
      {
        headerName: "FBM Fees",
        headerClass: "fbm-fees separate-border justify-content-center",
        children: [
          {
            headerName: "Total fee",
            field: "fbmReferralFee",
            filter: "agNumberColumnFilter",
            headerClass: "fbm-fees",
            cellRenderer: function (params: ICellRendererParams) {
              if (params.value !== undefined  && !params.data.isError) {
                return '$' + params.value;
              } else if(params.data.isError){
                return `<div class="calculate-validation">The Asin are <span>` + params.data.asin + `</span> invalid.</div>`
              } else {
                return getImage();
              }
            },
            cellClass: (params) => {
              return params.data.isError
                ? "border-right-cell justify-content-center"
                : null;
            },
            colSpan: (params) => {
              return params.data.isError ? 3 : 0;
            },
          },
          {
            headerName: "Break-even price",
            field: "breakEvenPrice.breakEvenPriceForFbm",
            filter: "agNumberColumnFilter",
            headerClass: "fbm-fees",
            cellRenderer: function (params: ICellRendererParams) {
              if (params.value !== undefined) {
                return '$' + params.value;
              } else {
                return getImage();
              }
            },
          },
          {
            headerName: "Min Price",
            field: "minPrice.minPriceForFbm",
            filter: "agNumberColumnFilter",
            headerClass: "fbm-fees",
            cellRenderer: function (params: ICellRendererParams) {
              if (params.value !== undefined) {
                return '$' + params.value;
              } else {
                return getImage();
              }
            },
            cellClass: (params) => {
              return !params.data.isError ? "border-right-cell" : null;
            },
          },
        ],
      },
    ];
    var getImage=()=> {
      return this.getLoaderImage()
   }
   return this.priceCalColDef;
  }

  getLoaderImage() {
    return '<img width="20px" src="../../../../assets/images/spinner2.gif">';
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }
}
